import React, { useEffect, useCallback, useState, useRef, Suspense, lazy } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useWebSocket, useUser } from '../WebSocketContext';
import ReactDOM from 'react-dom';
import WidgetDashboard from '../WidgetDashboard/WidgetDashboard';
import EventList from '../EventList/EventList';
import Settings from '../Settings';
import Header from './DashboardHeader'; // Import the new Header component
import { Box, Grid, Alert, IconButton, Card, Snackbar, Button, Typography, Skeleton, Fab, Drawer, Badge, Fade } from '@mui/material'; // Added Card, Snackbar, Button, Typography import
import EventIcon from '@mui/icons-material/Event';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';

import RefreshIcon from '@mui/icons-material/Refresh'; // Import RefreshIcon
import { DashboardProvider, useDashboard } from './DashboardContext'; // Import the context
import { useTranslation } from 'react-i18next'; // Import useTranslation
import Loading from '../Loading'; // Import the Loading component
import SettingDatasources from '../SettingDatasources';
import CloseButton from '../Common/CloseButton';
import WidgetPreviewStep from '../WidgetPreviewStep';

// Lazy load AddMediaOverlay for better initial page load
const AddMediaOverlay = lazy(() => import('../AddMediaOverlay'));
const TimelineEventsOptimized = lazy(() => import('../Timeline/TimelineEventsOptimized'));



// Styled Badge for the ripple effect
const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#ff1744',
    color: '#ff1744',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const DashboardContent = () => {
  const { caseid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { ws, subscribeToMessages, sendMessage } = useWebSocket();
  const user = useUser(); // Get user info with computed properties
  const { title, setTitle, description, setDescription, tags, setTags, deleteTimeEvent, setFilteredTimeEvents } = useDashboard(); // Use context
  const [folder, setFolder] = useState("");
  const [visibleSection, setVisibleSection] = useState("loading");
  const [status, setStatus] = useState("loading");
  const [dashboardOutdated, setDashboardOutdated] = useState(false);
  const [outlookFetchError, setOutlookFetchError] = useState(false);
  const [outlookTokenError, setOutlookTokenError] = useState(false);
  const [outlookApiReAuth, setOutlookApiReAuth] = useState(false);
  const [outlookApiAuthSuccess, setOutlookApiAuthSuccess] = useState(false);
  const [outlookApiAuthFailed, setOutlookApiAuthFailed] = useState(false);
  const [aiTextModelTokenInvalid, setAiTextModelTokenInvalid] = useState(false);
  const [postprocessingError, setPostprocessingError] = useState(false); // Add state for post-processing errors
  const [postprocessingErrorDetails, setPostprocessingErrorDetails] = useState(null); // Add state for error details
  const visibleSectionRef = useRef(visibleSection);
  const [inProgress, setInProgress] = useState(false); // New state for case progress
  const [datasourceCount, setDatasourceCount] = useState(0); // Add state for datasource count
  const [hasEvents, setHasEvents] = useState(false); // Track if case has any events
  const [showDashboardOverride, setShowDashboardOverride] = useState(false); // Allow viewing dashboard without events
  const [caseNotFound, setCaseNotFound] = useState(false); // Track missing case
  const [showCalendarWidget, setShowCalendarWidget] = useState(true);
  const [showTrendWidget, setShowTrendWidget] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' }); // Snackbar state
  const { t } = useTranslation(); // Use translation
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [eventToOpen, setEventToOpen] = useState(null);
  const showNewEventsBubble = dashboardOutdated && visibleSection === "timeline" && !hasEvents;



  // States for auto-opening modals after OAuth redirect
  const [autoOpenDatasource, setAutoOpenDatasource] = useState(null);
  const [autoOpenActions, setAutoOpenActions] = useState(null);
  const [autoOpenExportId, setAutoOpenExportId] = useState(null);
  const [autoOpenWidgetTag, setAutoOpenWidgetTag] = useState(null);
  const reviewWidgets = searchParams.get('reviewWidgets') === 'true';
  const headerOffset = reviewWidgets ? 0 : 56;

  // Check if user has read-only permissions using the user object
  const isReadOnly = user.isReadOnly;

  // No datasource dialog — we instead show datasources in the main timeline area when there are no events.
  // Keep handling autoOpenDatasource and autoOpenActions via state as before.
  // Handle OAuth redirect parameters to reopen modals
  useEffect(() => {
    const openDatasource = searchParams.get('openDatasource');
    const connector = searchParams.get('connector');
    const openActions = searchParams.get('openActions');
    const exportId = searchParams.get('exportId');
    const openWidgetSettings = searchParams.get('openWidgetSettings');
    const widgetTag = searchParams.get('widgetTag');

    let shouldUpdateParams = false;
    const nextParams = new URLSearchParams(searchParams);

    if (openDatasource === 'true' && connector) {
      setAutoOpenDatasource(connector);
      nextParams.delete('openDatasource');
      nextParams.delete('connector');
      shouldUpdateParams = true;
    }

    if (openActions === 'true') {
      setAutoOpenActions(true);
      if (exportId) {
        setAutoOpenExportId(exportId);
      }
      nextParams.delete('openActions');
      nextParams.delete('exportId');
      shouldUpdateParams = true;
    }

    if (openWidgetSettings === 'true') {
      if (widgetTag) {
        setAutoOpenWidgetTag(widgetTag);
      }
      setVisibleSection("timeline");
      nextParams.delete('openWidgetSettings');
      nextParams.delete('widgetTag');
      shouldUpdateParams = true;
    }

    if (shouldUpdateParams) {
      setSearchParams(nextParams);
    }
  }, [searchParams, setSearchParams]);

  const handleWidgetPreviewDone = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('reviewWidgets');
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  // Keep ref in sync with state
  useEffect(() => {
    visibleSectionRef.current = visibleSection;
  }, [visibleSection]);

  const handleWebSocketMessage = useCallback((message) => {
    switch (message.command) {
      case "getcase":
        // If server indicates the case doesn't exist, show a not-found view
        if (!message.data || !message.data.case) {
          console.log('Dashboard - case not found for id:', caseid);
          setCaseNotFound(true);
          setStatus('notfound');
          // clear any previous header content
          setTitle('');
          setDescription('');
          setFolder('');
          setTags([]);
          return;
        }

        setCaseNotFound(false);
        setTitle(message.data.case.title);
        setDescription(message.data.case.description);
        setFolder(message.data.case.folderlocation);
        setTags(message.data.case.customFields);
        const rawShowCalendar = message.data.case.showCalendarWidget;
        const rawShowTrend = message.data.case.showTrendWidget;
        setShowCalendarWidget(rawShowCalendar === undefined || rawShowCalendar === null ? true : !!rawShowCalendar);
        setShowTrendWidget(rawShowTrend === undefined || rawShowTrend === null ? true : !!rawShowTrend);

        var datasourceCount = message.data.datasource_count;
        var hasEvents = message.data.has_events || false;
        console.log('Dashboard - datasourceCount from server:', datasourceCount);
        console.log('Dashboard - hasEvents from server:', hasEvents);
        console.log('Dashboard - case data:', message.data.case);
        setDatasourceCount(datasourceCount); // Set the datasource count
        setHasEvents(hasEvents); // Set whether case has events
        // Convert numeric value to boolean
        setInProgress(!!message.data.case.InProgress); // Updated line

        if (visibleSectionRef.current === "loading") {
          setVisibleSection("timeline"); // Fallback to timeline if still in loading state
        }
        setStatus("loaded");
        break;
      case "getOtherShowCalendarWidget": {
        const payload = message.data || {};
        if (payload.status === 'success') {
          const value = payload.show_calendar_widget;
          setShowCalendarWidget(value === undefined || value === null ? true : !!value);
        }
        break;
      }
      case "updateOtherShowCalendarWidget": {
        const payload = message.data || {};
        if (payload.status === 'success') {
          const value = payload.show_calendar_widget;
          setShowCalendarWidget(value === undefined || value === null ? true : !!value);
        }
        break;
      }
      case "getOtherShowTrendWidget": {
        const payload = message.data || {};
        if (payload.status === 'success') {
          const value = payload.show_trend_widget;
          setShowTrendWidget(value === undefined || value === null ? true : !!value);
        }
        break;
      }
      case "updateOtherShowTrendWidget": {
        const payload = message.data || {};
        if (payload.status === 'success') {
          const value = payload.show_trend_widget;
          setShowTrendWidget(value === undefined || value === null ? true : !!value);
        }
        break;
      }
      case "deleteTimeEvent":
        // Handle time event deletion
        if (message.data.success) {
          // Remove the deleted time event from the state using the context function
          deleteTimeEvent(message.data.id);
          // Also remove from filtered time events if they exist
          setFilteredTimeEvents(prevFiltered =>
            prevFiltered.filter(event => parseInt(event.id) !== parseInt(message.data.id))
          );
          // Show success snackbar
          setSnackbar({
            open: true,
            message: t('timeevent.delete_success') || 'Time event deleted successfully',
            severity: 'success'
          });
        }
        break;

      case "notifications":
        console.log("Received notification:", message.data);
        console.log("Message ID:", message.data.id, "Case ID:", caseid);
        console.log("Message Type:", message.data.type);

        if (parseInt(message.data.id) !== parseInt(caseid)) {
          console.log("Message ID does not match case ID, skipping...");
          return;
        }

        if (message.data.type === "file_changed_completed") {
          console.log("Timeline updated found");
          setDashboardOutdated(true);
        } else if (message.data.type === "invalid_outlook_token") {
          console.log("invalid outlook token");
          setOutlookTokenError(true);
        } else if (message.data.type === "error_fetching_emails_from_outlook_api") {
          console.log("error fetching emails from outlook api");
          setOutlookFetchError(true);
        } else if (message.data.type === "reauthenticate_outlook_api") {
          console.log("reauthenticate outlook api");
          setOutlookApiReAuth(true);
        } else if (message.data.type === "outlook_api_auth_success") {
          console.log("outlook api auth success");
          setOutlookApiAuthSuccess(true);
        } else if (message.data.type === "outlook_api_auth_failed") {
          console.log("outlook api auth failed");
          setOutlookApiAuthFailed(true);
        } else if (message.data.type === "invalid_ai_text_model_token") {
          console.log("invalid ai text model token");
          setAiTextModelTokenInvalid(true);
        } else if (message.data.type === "postprocessing_error") {
          console.log("postprocessing error", message.data);
          setPostprocessingError(true);
          setPostprocessingErrorDetails({
            dataexport_id: message.data.dataexport_id,
            dataexport_name: message.data.dataexport_name,
            error_message: message.data.error_message,
            timestamp: message.data.timestamp
          });
        }
        break;
      default:
        break;
    }
  }, [caseid, deleteTimeEvent, setDescription, setFilteredTimeEvents, setTags, setTitle, t, sendMessage]);

  const getCase = useCallback(() => {
    sendMessage({ command: 'getcase', caseid });
  }, [sendMessage, caseid]);

  useEffect(() => {
    if (ws) {
      setTitle("Loading...");
      setDescription("Loading...");
      setStatus("loading");
      setCaseNotFound(false);
      setFolder("Loading...");
      getCase();
      console.log("Getting case");
    }
  }, [ws, caseid, getCase, setTitle, setDescription]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      // Message is already parsed in WebSocketContext
      handleWebSocketMessage(parsedMessage);
    });
    return unsubscribe;
  }, [subscribeToMessages, handleWebSocketMessage]);

  const openFolder = () => {
    sendMessage({ command: 'openfolder', caseid });
  };

  const RenameAll = () => {
    sendMessage({ command: 'renameall', caseid });
  };
  const refreshPage = () => {
    window.location.reload();
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };



  // Show loading spinner when still loading time events
  const renderMainContent = () => {
    if (status === "loading") {
      return (
        <Box sx={{
          width: '100%',
          height: '100%',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          pt: 1,
          contain: 'layout style', // Improve layout performance
        }}>
          <Card sx={{ boxShadow: 6, width: '100%', margin: "10px", borderRadius: 5, minHeight: '30vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '30vh' }}>
              <Loading />
            </Box>
          </Card>
        </Box>
      );
    }

    // If we're showing settings, always render the settings component
    if (visibleSection === "settings") {
      return (
        <Box sx={{
          backgroundColor: '#f7f7ff',
          background: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 70%, rgba(87, 87, 176, 0.08) 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: headerOffset ? `calc(100vh - ${headerOffset}px)` : '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 1,
          contain: 'layout style', // Improve layout performance
        }}>
          <Grid container justifyContent="center">
            <Grid item xs={12} md={11.5}>
              <Settings caseid={caseid} tags={tags} />
            </Grid>
          </Grid>
        </Box>
      );
    }
    // Show datasources setup when no events exist yet — instead of returning the whole page,
    // we now open a dialog with the settings, while still rendering the dashboard behind it.
    console.log('Dashboard render - datasourceCount:', datasourceCount, 'hasEvents:', hasEvents, 'status:', status, 'visibleSection:', visibleSection);

    console.log('Dashboard - Showing main content (WidgetDashboard or EventList)');
    return (
      <Box sx={{
        backgroundColor: '#f7f7ff',
        background: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 70%, rgba(87, 87, 176, 0.08) 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: headerOffset ? `calc(100vh - ${headerOffset}px)` : '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: reviewWidgets && !hasEvents ? 'center' : 'flex-start',
        overflow: 'hidden', // Prevent horizontal scroll
        contain: 'layout style', // Improve layout performance
      }}>
        {visibleSection === "timeline" && !isReadOnly && (
          <Suspense fallback={null}>
            <AddMediaOverlay caseid={caseid} fullPage />
          </Suspense>
        )}
        <Box sx={{ padding: visibleSection === "eventlist" ? 0 : 1, width: '100%' }}>
          <Grid container justifyContent="center">
            <Grid item xs={12} md={visibleSection === "eventlist" ? 12 : 11.5}>
              {visibleSection === "timeline" && !hasEvents && !showDashboardOverride && (
                <>
                  <Card sx={{ boxShadow: 6, width: '100%', margin: "10px", borderRadius: 5, minHeight: '50vh' }}>
                    <Box sx={{ p: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                      <Box sx={{ textAlign: 'center', width: '100%', maxWidth: '900px' }}>
                        {reviewWidgets ? (
                          <WidgetPreviewStep
                            caseid={caseid}
                            onContinue={handleWidgetPreviewDone}
                          />
                        ) : (
                          <>
                          <Box sx={{ mt: 2, mb: 4, textAlign: 'center', width: '100%' }}>
                            <Typography
                              variant="h5"
                              component="h2"
                              sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}
                            >
                              {t('dashboard.connect_data_sources_title', 'Connect your data sources')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680, mx: 'auto' }}>
                              {t('dashboard.connect_data_sources_subtitle', 'Connect email, cloud drives, and integrations to populate this dashboard with documents and events.')}
                            </Typography>
                          </Box>

                            <SettingDatasources
                              caseid={caseid}
                              hideHeader={true}
                              autoOpenConnector={autoOpenDatasource}
                              onConnectorOpened={() => { setAutoOpenDatasource(null); }}
                            />
                            <Box sx={{ mt: 2 }}>
                              <Button
                                variant="text"
                                color="primary"
                                onClick={() => setShowDashboardOverride(true)}
                              >
                                {t('dashboard.continue_to_dashboard', 'Continue to Dashboard without adding data')}
                              </Button>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Card>
                </>
              )}
              {visibleSection === "timeline" && (hasEvents || showDashboardOverride) && (
                <>
                  <WidgetDashboard
                    caseid={caseid}
                    folder={folder}
                    showCalendarWidget={showCalendarWidget}
                    showTrendWidget={showTrendWidget}
                    onOpenTimeline={(id) => { setEventToOpen(id); setDrawerOpen(true); }}
                    autoOpenWidgetTag={autoOpenWidgetTag}
                    onAutoOpenWidgetTagHandled={() => setAutoOpenWidgetTag(null)}
                  />
                </>
              )}
              {visibleSection === "eventlist" && <EventList caseid={caseid} folder={folder} />}
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  return caseNotFound ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
      <Card sx={{ maxWidth: 800, width: '100%', p: 3, borderRadius: 4, boxShadow: 6 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold' }}>{t('dashboard.project_not_found_title')}</Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>{t('dashboard.project_not_found_message')}</Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            {t('dashboard.back_to_projects')}
          </Button>
        </Box>
      </Card>
    </Box>
  ) : (
    <>
      {!reviewWidgets && (
        <Header
          title={title}
          setTitle={setTitle}
          description={description}
          setDescription={setDescription}
          folder={folder}
          setVisibleSection={setVisibleSection}
          RenameAll={RenameAll}
          openFolder={openFolder}
          ws={ws}
          caseid={caseid}
          tags={tags}
          visibleSection={visibleSection}
          isReadOnly={isReadOnly}
          autoOpenDatasource={autoOpenDatasource}
          onDatasourceOpened={() => setAutoOpenDatasource(null)}
          autoOpenActions={autoOpenActions}
          autoOpenExportId={autoOpenExportId}
          onActionsOpened={() => {
            setAutoOpenActions(null);
            setAutoOpenExportId(null);
          }}
        />
      )}

      {inProgress && (
        <Alert severity="info">{t('dashboard.case_in_progress')}</Alert>
      )}
      {outlookTokenError && (
        // Explain the error briefly, and formulate url to the case settings page in the alert message
        <Alert
          severity="error"
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setVisibleSection("settings")}
            >
              {t('dashboard.navigate_to_settings')}
            </IconButton>
          }
        >
          {t('dashboard.error_fetching_outlook_emails_bad_credential')}
        </Alert>
      )}
      {outlookFetchError && (
        // Explain the error briefly, and ask user to go to the case settings page in the alert message
        <Alert
          severity="error"
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setVisibleSection("settings")}
            >
              {t('dashboard.navigate_to_settings')}
            </IconButton>
          }
        >
          {t('dashboard.error_fetching_outlook_emails')}
        </Alert>
      )}
      {outlookApiReAuth && (
        // Explain the error briefly, and ask user to go to the case settings page in the alert message
        <Alert
          severity='error'
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setVisibleSection("settings")}
            >
              {t('dashboard.navigate_to_settings')}
            </IconButton>
          }
        >
          {t('dashboard.reverify_outlook_account')}
        </Alert>
      )}
      {outlookApiAuthSuccess && (
        <Alert
          severity='info'
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setVisibleSection("settings")}
            >
              {t('dashboard.navigate_to_settings')}
            </IconButton>
          }
        >
          {t('dashboard.complete_outlook_settings')}
        </Alert>
      )}
      {outlookApiAuthFailed && (
        <Alert
          severity='error'
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setVisibleSection("settings")}
            >
              {t('dashboard.navigate_to_settings')}
            </IconButton>
          }
        >
          {t('dashboard.verify_outlook_account_failed')}
        </Alert>
      )}
      {aiTextModelTokenInvalid && (
        <Alert
          severity='error'
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => {
                // navigate to the settings page from the root domain
                navigate('/Settings');
              }}
            >
              {t('dashboard.navigate_to_settings')}
            </IconButton>
          }
        >
          {t('dashboard.invalid_ai_text_model_token')}
        </Alert>
      )}
      {postprocessingError && postprocessingErrorDetails && (
        <Alert
          severity='error'
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => {
                setPostprocessingError(false);
                setPostprocessingErrorDetails(null);
              }}
            >
              {t('dashboard.dismiss') || 'Dismiss'}
            </IconButton>
          }
        >
          <Box>
            <Box sx={{ fontWeight: 'bold', marginBottom: 1 }}>
              Post-processing Error
            </Box>
            <Box sx={{ fontSize: '0.9em', marginBottom: 0.5 }}>
              <strong>Action:</strong> {postprocessingErrorDetails.dataexport_name} (ID: {postprocessingErrorDetails.dataexport_id})
            </Box>
            <Box sx={{ fontSize: '0.9em', marginBottom: 0.5 }}>
              <strong>Error:</strong> {postprocessingErrorDetails.error_message}
            </Box>
            <Box sx={{ fontSize: '0.8em', opacity: 0.7 }}>
              {new Date(postprocessingErrorDetails.timestamp).toLocaleString()}
            </Box>
          </Box>
        </Alert>
      )}

      {renderMainContent()}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Timeline FAB + Drawer so they remain visible and usable when Datasources panel is shown */}
      {ReactDOM.createPortal(
        <Box sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          zIndex: 1000
        }}>
          {showNewEventsBubble && (
            <Fade in timeout={240}>
              <Box
                sx={{
                  position: 'relative',
                  px: 1.5,
                  py: 0.6,
                  borderRadius: 999,
                  backgroundColor: '#fff',
                  color: '#d50000',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  boxShadow: '0 10px 24px rgba(255, 23, 68, 0.25)',
                  border: '1px solid rgba(255, 23, 68, 0.45)',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    right: -5,
                    width: 10,
                    height: 10,
                    backgroundColor: '#fff',
                    borderRight: '1px solid rgba(255, 23, 68, 0.45)',
                    borderTop: '1px solid rgba(255, 23, 68, 0.45)',
                    transform: 'translateY(-50%) rotate(45deg)'
                  }
                }}
              >
                {t('dashboard.new_events', 'New Events')}
              </Box>
            </Fade>
          )}
          <Tooltip title={dashboardOutdated ? t("dashboard.new_data_available", "New updates available") : "Timeline"} arrow placement="left">
            <Fab
              color={dashboardOutdated ? "error" : "primary"}
              aria-label="timeline"
              onClick={() => {
                setDrawerOpen(true);
                setDashboardOutdated(false);
                sendMessage({ command: 'getcase', caseid });
                if (visibleSection !== "timeline") {
                  setVisibleSection("timeline");
                }
              }}
              sx={{
                backgroundColor: dashboardOutdated ? '#ff1744' : '#5757b0', // Red if outdated
                boxShadow: dashboardOutdated
                  ? '0 0 20px rgba(255, 23, 68, 0.6)'
                  : '0 18px 36px rgba(87, 87, 176, 0.35)',
                transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.27, 1.55)', // Bouncy transition
                width: { xs: 48, sm: 56 },
                height: { xs: 48, sm: 56 },
                // ... existing animations logic if needed, or we can simplify
                animation: dashboardOutdated
                  ? 'fabShake 0.5s ease-in-out infinite alternate'
                  : (isMobile ? 'none' : 'fabBounce 4.2s ease-in-out infinite'),

                '&:hover': {
                  backgroundColor: dashboardOutdated ? '#d50000' : '#7777d0',
                  transform: 'translateY(-3px) scale(1.05)',
                  boxShadow: dashboardOutdated
                    ? '0 0 30px rgba(255, 23, 68, 0.8)'
                    : '0 22px 44px rgba(87, 87, 176, 0.45)'
                },
                '@keyframes fabShake': {
                  '0%': { transform: 'rotate(-5deg)' },
                  '100%': { transform: 'rotate(5deg)' }
                },
                '@keyframes fabBounce': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-6px)' }
                },
                // ... keep other keyframes if they don't conflict
              }}
            >
              <StyledBadge
                overlap="circular"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                variant="dot"
                invisible={!dashboardOutdated}
              >
                <EventIcon />
              </StyledBadge>
            </Fab>
          </Tooltip>
        </Box>,
        document.body
      )}

      {ReactDOM.createPortal(
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{
            container: document.body,
            style: { position: 'fixed' }
          }}
          SlideProps={{
            direction: 'left'
          }}
          sx={{
            '& .MuiDrawer-paper': {
              right: 0,
              left: 'auto',
              width: { xs: '100%', sm: '90%', md: '70%', lg: '60%' },
              maxWidth: '1200px',
              height: '100vh',
              maxHeight: '100vh',
              background: 'linear-gradient(180deg, #ffffff 0%, #f5f5ff 55%, #f0f0ff 100%)',
              backgroundSize: '170% 170%',
              animation: 'timelineDrawerSlide 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both, timelineDrawerGlow 22s ease-in-out infinite alternate',
              boxShadow: '0 24px 60px rgba(15, 23, 42, 0.35)',
              borderTopLeftRadius: { xs: 0, md: 24 },
              borderBottomLeftRadius: { xs: 0, md: 24 },
              borderLeft: { xs: 'none', md: '1px solid rgba(87, 87, 176, 0.15)' },
              overflow: 'hidden',
              isolation: 'isolate',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(120deg, transparent 10%, rgba(87,87,176,0.06) 40%, transparent 70%)',
                opacity: 0.5,
                pointerEvents: 'none',
                animation: 'timelineDrawerSheen 12s ease-in-out infinite'
              }
            },
            '@keyframes timelineDrawerSlide': {
              '0%': { opacity: 0, transform: 'translateX(40px)' },
              '100%': { opacity: 1, transform: 'translateX(0)' }
            },
            '@keyframes timelineDrawerGlow': {
              '0%': { backgroundPosition: '0% 0%' },
              '100%': { backgroundPosition: '0% 90%' }
            },
            '@keyframes timelineDrawerSheen': {
              '0%, 60%': { opacity: 0, transform: 'translateX(0)' },
              '70%': { opacity: 0.5, transform: 'translateX(18%)' },
              '100%': { opacity: 0, transform: 'translateX(35%)' }
            }
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0',
                position: 'sticky',
                top: 0,
                background: 'rgba(255,255,255,0.94)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 6px 18px rgba(87, 87, 176, 0.12)',
                zIndex: 1,
                animation: 'drawerHeaderDrop 0.4s ease both',
                '@keyframes drawerHeaderDrop': {
                  '0%': { opacity: 0, transform: 'translateY(-12px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' }
                }
              }}
            >
              <Typography variant="h6" sx={{ color: '#5757b0', fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {t('timeline.title', 'Timeline')}
              </Typography>
              <CloseButton
                onClick={() => setDrawerOpen(false)}
                sx={{
                  transition: 'transform 0.25s ease, color 0.25s ease',
                  '&:hover': {
                    transform: 'rotate(90deg) scale(1.05)',
                    color: '#d32f2f'
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 1, sm: 0 } }}>
              <Suspense fallback={<Loading />}>
                <TimelineEventsOptimized
                  caseid={caseid}
                  folder={folder}
                  initialEventId={eventToOpen}
                  onClose={() => { setDrawerOpen(false); setEventToOpen(null); }}
                />
              </Suspense>
            </Box>
          </Box>
        </Drawer>,
        document.body
      )}
    </>
  );
};


// Wrap the DashboardContent in the DashboardProvider to pass the context
const Dashboard = () => {
  const { caseid } = useParams();
  return (
    <DashboardProvider key={caseid}>
      <DashboardContent />
    </DashboardProvider>
  );
};

export default Dashboard;
